import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api, { formatDate, STATUS_LABELS } from '../lib/api';
import { Check, Clock, Calendar, Users, AlertTriangle, MapPin } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';

export default function Confirmation() {
  const { id } = useParams();
  const [r, setR] = useState(null);
  const [settings, setSettings] = useState(null);
  const [tagsCatalog, setTagsCatalog] = useState([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/reservations/${id}`).then(res => setR(res.data)).catch(() => setError(true));
    api.get('/settings/public').then(res => setSettings(res.data)).catch(() => {});
    api.get('/tags/public').then(res => setTagsCatalog(res.data || [])).catch(() => {});
  }, [id]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F7]">
        <div className="text-center">
          <p className="text-[#5C5C59] mb-4">Reservierung nicht gefunden.</p>
          <Link to="/" className="btn-primary px-6 py-3 rounded">Zurück</Link>
        </div>
      </div>
    );
  }

  if (!r) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F7]">
        <div className="w-10 h-10 border-2 border-[#1C1C19] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const isPending = r.status === 'pending';
  const isConfirmed = r.status === 'confirmed';
  const isDeclined = r.status === 'declined' || r.status === 'cancelled';

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="confirmation-page">
      <div className="max-w-2xl mx-auto px-4 md:px-6 py-8 md:py-16">
        <div className="mb-10 md:mb-12">
          <BrandLogo
            logoUrl={settings?.logo_url}
            logoText={settings?.logo_text || settings?.restaurant_name}
            testId="conf-logo"
          />
        </div>

        <div className="bg-white border border-[#E5E5E0] rounded-lg overflow-hidden animate-fade-up">
          <div className="confirmation-image h-32 md:h-48 relative">
            <div className="absolute inset-0 bg-[#1C1C19]/30"></div>
          </div>

          <div className="p-6 md:p-10">
            <div className="flex items-center gap-3 mb-6">
              {isConfirmed && (
                <span className="w-12 h-12 rounded-full bg-[#22C55E]/15 flex items-center justify-center">
                  <Check className="w-6 h-6 text-[#22C55E]" />
                </span>
              )}
              {isPending && (
                <span className="w-12 h-12 rounded-full bg-[#EAB308]/15 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-[#EAB308]" />
                </span>
              )}
              {isDeclined && (
                <span className="w-12 h-12 rounded-full bg-[#EF4444]/15 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-[#EF4444]" />
                </span>
              )}
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#5C5C59]" data-testid="conf-status-label">
                  {STATUS_LABELS[r.status]}
                </p>
                <h1 className="font-display text-2xl md:text-3xl text-[#1C1C19]" data-testid="conf-title">
                  {isConfirmed && 'Reservierung bestätigt'}
                  {isPending && 'Reservierung eingegangen'}
                  {isDeclined && 'Reservierung abgelehnt'}
                </h1>
              </div>
            </div>

            {isPending && (
              <p className="text-[#5C5C59] mb-6">
                Wir prüfen Ihre Anfrage und informieren Sie in Kürze über die Bestätigung.
              </p>
            )}
            {isConfirmed && (
              <p className="text-[#5C5C59] mb-6">
                Wir freuen uns auf Ihren Besuch. Bei Änderungen kontaktieren Sie uns bitte direkt.
              </p>
            )}

            <div className="border-t border-[#E5E5E0] pt-6 space-y-4">
              <DetailRow icon={Calendar} label="Datum" value={formatDate(r.date)} testId="conf-date" />
              <DetailRow icon={Clock} label="Uhrzeit" value={`${r.start_time} – ${r.end_time}`} testId="conf-time" />
              <DetailRow icon={Users} label="Personen" value={r.persons} testId="conf-persons" />
              <DetailRow icon={MapPin} label="Name" value={r.customer_name} testId="conf-name" />
              {r.is_short_slot && (
                <div className="bg-[#FFFBEB] border border-[#EAB308] rounded p-3 text-sm flex gap-2" data-testid="conf-short-slot">
                  <Clock className="w-4 h-4 text-[#EAB308] flex-shrink-0 mt-0.5" />
                  <span>Kurzzeit-Reservierung – Tisch wird nach Ablauf für den nächsten Gast benötigt.</span>
                </div>
              )}
              {r.note && (
                <div className="border-t border-[#E5E5E0] pt-4">
                  <p className="text-xs uppercase tracking-wider text-[#5C5C59] mb-1">Ihre Bemerkung</p>
                  <p className="text-sm text-[#1C1C19]">{r.note}</p>
                </div>
              )}
              {Array.isArray(r.tag_ids) && r.tag_ids.length > 0 && tagsCatalog.length > 0 && (
                <div className="border-t border-[#E5E5E0] pt-4" data-testid="conf-tags">
                  <p className="text-xs uppercase tracking-wider text-[#5C5C59] mb-2">Ihre Wünsche</p>
                  <div className="flex flex-wrap gap-1.5">
                    {r.tag_ids.map(tid => {
                      const t = tagsCatalog.find(x => x.id === tid);
                      if (!t) return null;
                      return (
                        <span
                          key={tid}
                          data-testid={`conf-tag-${tid}`}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs"
                          style={{ background: (t.color || '#9CA3AF') + '22', color: t.color || '#1C1C19' }}
                        >
                          {t.icon && <span>{t.icon}</span>}
                          {t.name}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
              {Array.isArray(r.selected_offers) && r.selected_offers.length > 0 && (
                <div className="border-t border-[#E5E5E0] pt-4" data-testid="conf-offers">
                  <p className="text-xs uppercase tracking-wider text-[#5C5C59] mb-2">Ihre Vorbestellung</p>
                  <div className="space-y-1">
                    {r.selected_offers.map((o, i) => (
                      <div key={o.offer_id || i} className="flex justify-between text-sm text-[#1C1C19]" data-testid={`conf-offer-${o.offer_id || i}`}>
                        <span><span className="font-medium">{o.quantity}×</span> {o.name}</span>
                        {Number(o.price) > 0 && <span className="text-[#5C5C59] font-mono">{((o.quantity || 1) * (Number(o.price) || 0)).toFixed(2).replace('.', ',')} €</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-8 pt-6 border-t border-[#E5E5E0] text-sm text-[#5C5C59]">
              <p className="font-mono">Reservierungsnummer: <span className="text-[#1C1C19]" data-testid="conf-id">{r.id}</span></p>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              <Link to={`/r/${r.id}`} data-testid="conf-manage-link" className="inline-block bg-[#1C1C19] hover:bg-[#3A3A35] text-white px-6 py-3 rounded text-sm">
                Reservierung verwalten
              </Link>
              <Link to="/" className="inline-block btn-outline px-6 py-3 rounded text-sm" data-testid="conf-home-link">
                Zurück zur Startseite
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ icon: Icon, label, value, testId }) {
  return (
    <div className="flex items-start gap-3" data-testid={testId}>
      <Icon className="w-5 h-5 text-[#5C5C59] mt-0.5" />
      <div>
        <p className="text-xs uppercase tracking-wider text-[#5C5C59]">{label}</p>
        <p className="text-base text-[#1C1C19] font-medium">{value}</p>
      </div>
    </div>
  );
}
